import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { useMultiTenant } from '@/hooks/useMultiTenant';
import { Purchase, PurchaseWithDetails, PurchaseFilters } from '@/types/purchase';
import { useToast } from '@/hooks/use-toast';
import { auditService } from '@/services/auditService';
import { toDateOnlyLocal } from '@/utils/dateHelpers';

// §I.143-B lote 1 — contratos do motor atômico. Retornos verbatim dos corpos
// vivos (espelhos P3/P4/P5, E-L1.1); erros de negócio chegam por EXCEPTION e
// sobem ao toast via error.message.
export interface PurchaseItemInput {
  product_id?: string | null;
  product_name: string;
  product_sku?: string | null;
  quantity: number;
  measurement_unit?: string | null;
  unit_conversion?: number | null;
  /** I.151 1b3-a: unidade alternativa do produto (product_units.id); null = unidade base. O banco converte. */
  product_unit_id?: string | null;
  supplier_product_code?: string | null;
  unit_price: number;
  discount_value?: number | null;
  item_type?: string | null;
  item_details?: string | null;
  notes?: string | null;
}

export type CreatePurchaseInput = Partial<Purchase> & {
  items: (PurchaseItemInput & Record<string, unknown>)[];
  receive?: boolean;
};

export interface CreatePurchaseResult {
  success: boolean;
  purchase_id: string;
  purchase_number: string;
  status: string;
  payables_generated: number;
}

export interface UpdatePurchaseInput {
  id: string;
  header?: Record<string, unknown>;
  items_update?: (Partial<PurchaseItemInput> & { id: string })[];
  items_insert?: (PurchaseItemInput & Record<string, unknown>)[];
  items_delete?: string[];
}

export interface UpdatePurchaseResult {
  success: boolean;
  purchase_id: string;
  items_updated: number;
  items_inserted: number;
  items_deleted: number;
  payables_regenerated: number;
}

export interface ReceivePurchaseInput {
  purchaseId: string;
  items: { item_id: string; quantity: number }[];
}

export interface ReceivePurchaseResult {
  success: boolean;
  purchase_id: string;
  new_status: string;
  deposit_id: string;
  items_received: number;
  movements_created: number;
}

export const usePurchases = (filters?: PurchaseFilters, externalPagination?: { page: number; pageSize: number }, externalSetPagination?: (p: { page: number; pageSize: number }) => void) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { companyId } = useMultiTenant();
  const [internalPagination, setInternalPagination] = useState({ page: 1, pageSize: 50 });
  
  const pagination = externalPagination || internalPagination;
  const setPagination = externalSetPagination || setInternalPagination;

  const query = useQuery({
    queryKey: ['purchases', companyId, filters, pagination],
    queryFn: async () => {
      if (!companyId) throw new Error("Company ID não disponível");

      const { data: purchasesData, error: purchasesError } = await supabase.rpc(
        'get_purchases_filtered',
        {
          p_company_id: companyId,
          p_search: filters?.searchTerm || null,
          p_status: filters?.status || null,
          p_movement_type: filters?.movementType || null,
          p_supplier_id: filters?.supplierId || null,
          p_start_date: filters?.startDate ? toDateOnlyLocal(filters.startDate) : null,
          p_end_date: filters?.endDate ? toDateOnlyLocal(filters.endDate) : null,
          p_limit: pagination.pageSize,
          p_offset: (pagination.page - 1) * pagination.pageSize,
        }
      );

      if (purchasesError) throw purchasesError;

      const mappedData = purchasesData?.map((purchase: any) => ({
        ...purchase,
        supplier: {
          id: purchase.supplier_id,
          name: purchase.supplier_name,
        },
        items: purchase.items || [],
      })) || [];

      return {
        purchases: mappedData as PurchaseWithDetails[],
        totalCount: purchasesData?.[0]?.total_count || 0
      };
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const { data: stats } = useQuery({
    queryKey: ['purchases-stats', companyId, filters?.startDate, filters?.endDate],
    queryFn: async () => {
      if (!companyId) throw new Error("Company ID não disponível");

      const { data: statsData, error: statsError } = await supabase.rpc(
        'get_purchases_stats',
        {
          p_company_id: companyId,
          p_start_date: filters?.startDate ? toDateOnlyLocal(filters.startDate) : null,
          p_end_date: filters?.endDate ? toDateOnlyLocal(filters.endDate) : null,
        }
      );

      if (statsError) throw statsError;
      return statsData?.[0] || {};
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  // defaultDeposit query removed (BUG-C20); §I.143: movimentação de estoque é
  // do motor atômico (E-A1 — depósito principal resolvido server-side)

  const createMutation = useMutation({
    mutationFn: async (purchase: CreatePurchaseInput): Promise<CreatePurchaseResult> => {
      if (!companyId) {
        throw new Error('Empresa não identificada. Faça login novamente.');
      }

      const { items, receive, ...header } = purchase;

      // E-L1.3: receive e status nunca divergentes no envelope — receive
      // explícito força status 'received'; ausente, deriva do status.
      const effectiveReceive = receive ?? header.status === 'received';
      const p_payload = {
        ...header,
        status: effectiveReceive ? 'received' : (header.status ?? 'draft'),
        receive: effectiveReceive,
        items,
      };

      const { data, error } = await supabase.rpc('create_purchase_atomic', {
        p_payload: p_payload as unknown as Json,
      });
      if (error) throw error;

      const result = data as unknown as CreatePurchaseResult;
      if (!result || result.success !== true) {
        throw new Error('Falha ao criar compra');
      }
      return result;
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['purchases-stats'] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['stock-summary'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['purchases-pending-widget'] });
      // Ramo receive gera payables CP-8 dentro do motor
      queryClient.invalidateQueries({ queryKey: ['accounts-payable'] });
      queryClient.invalidateQueries({ queryKey: ['payables-stats'] });

      await auditService.logAction({
        action: 'CRIAÇÃO',
        module: 'COMPRAS',
        entityType: 'compra',
        entityId: data.purchase_id,
        description: `Compra criada: ${data.purchase_number}`,
        details: {
          purchase_number: data.purchase_number,
          status: data.status,
          payables_generated: data.payables_generated,
        }
      });

      const statusMessage = data.status === 'received'
        ? 'Compra criada e recebida! Estoque lançado no depósito principal.'
        : 'Pedido salvo com sucesso!';

      toast({ title: statusMessage });
    },
    onError: (error) => {
      console.error('[usePurchases] Erro na criação:', error);
      toast({
        title: 'Erro ao criar compra',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...payload }: UpdatePurchaseInput): Promise<UpdatePurchaseResult> => {
      // E-L1.4: tripwire contra payload legado (terra-arrasada). O contrato novo
      // exige ao menos uma das chaves do diff; payload antigo falha alto, nunca no-op.
      if (!payload.header && !payload.items_update && !payload.items_insert && !payload.items_delete) {
        throw new Error('Payload de atualização em formato legado — contrato exige header/items_update/items_insert/items_delete');
      }
      const { data, error } = await supabase.rpc('update_purchase_atomic', {
        p_purchase_id: id,
        p_payload: payload as unknown as Json,
      });
      if (error) throw error;

      const result = data as unknown as UpdatePurchaseResult;
      if (!result || result.success !== true) {
        throw new Error('Falha ao atualizar compra');
      }
      return result;
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['purchases-stats'] });
      queryClient.invalidateQueries({ queryKey: ['accounts-payable'] });
      queryClient.invalidateQueries({ queryKey: ['payables-stats'] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['stock-summary'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['purchases-pending-widget'] });

      await auditService.logAction({
        action: 'ATUALIZAÇÃO',
        module: 'COMPRAS',
        entityType: 'compra',
        entityId: data.purchase_id,
        description: `Compra atualizada (${data.items_updated} alterados, ${data.items_inserted} inseridos, ${data.items_deleted} removidos)`,
        details: {
          items_updated: data.items_updated,
          items_inserted: data.items_inserted,
          items_deleted: data.items_deleted,
          payables_regenerated: data.payables_regenerated,
        }
      });

      // A-oct.2: plural pontual (D-F1.4 — sem helper genérico); "1 parcelas" morre aqui
      const n = data.payables_regenerated;
      const statusMessage = n > 0
        ? `Compra atualizada — financeiro regenerado (${n} ${n === 1 ? 'parcela' : 'parcelas'})`
        : 'Compra atualizada com sucesso!';

      toast({ title: statusMessage });
    },
    onError: (error) => {
      console.error('[usePurchases] Erro na atualização:', error);
      // R-COMPRAS.19: a trava fiscal do motor (update_purchase_atomic) fala com prefixo fixo —
      // vira toast legível, sem o rótulo genérico "Erro ao atualizar compra"
      const FISCAL_PREFIX = 'Compra vinculada a nota fiscal:';
      const isFiscalLock = (error.message ?? '').startsWith(FISCAL_PREFIX);
      const fiscalDetail = isFiscalLock ? error.message.slice(FISCAL_PREFIX.length).trim() : '';
      toast({
        title: isFiscalLock ? 'Compra travada pela nota fiscal' : 'Erro ao atualizar compra',
        description: isFiscalLock
          ? fiscalDetail.charAt(0).toUpperCase() + fiscalDetail.slice(1)
          : error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Use RPC function for cascading delete with proper permissions
      const { data, error } = await supabase.rpc('delete_purchase_with_cascade', {
        p_purchase_id: id,
        p_reason: 'Exclusão manual'
      });

      if (error) throw error;
      
      // Verificar se a função retornou sucesso
      if (data && typeof data === 'object' && 'success' in data && !data.success) {
        throw new Error((data as { error?: string }).error || 'Erro ao excluir compra');
      }
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['purchases-stats'] });
      // BUG-P30: Invalidate edit cache
      queryClient.invalidateQueries({ queryKey: ['purchase-for-edit'] });
      // Cascade: invalidate accounts payable (linked records are deleted)
      queryClient.invalidateQueries({ queryKey: ['accounts-payable'] });
      queryClient.invalidateQueries({ queryKey: ['payables-stats'] });
      // Cascade: invalidate stock queries (quantities reverted by RPC)
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['stock-summary'] });
      queryClient.invalidateQueries({ queryKey: ['stock-summary-stats'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product-warehouse-stock'] });
      queryClient.invalidateQueries({ queryKey: ['purchases-pending-widget'] });
      // A-tric.9 (carona F2): delete_purchase_with_cascade devolve a pendência DFe a 'pending'
      // (SQL da mão do Diretor) — a lista de Notas de Entrada precisa refletir sem F5
      queryClient.invalidateQueries({ queryKey: ['pending-purchase-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-invoice-counts'] });

      toast({ title: 'Compra e lançamentos vinculados excluídos com sucesso' });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao excluir compra',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const receiveItemsMutation = useMutation({
    mutationFn: async ({ purchaseId, items }: ReceivePurchaseInput): Promise<ReceivePurchaseResult> => {
      // Deltas {item_id, quantity}; depósito principal, guardas de status e
      // status derivado são todos server-side (motor atômico, E-A1/E-A2/E-B3)
      const { data, error } = await supabase.rpc('receive_purchase_atomic', {
        p_purchase_id: purchaseId,
        p_items: items as unknown as Json,
      });
      if (error) throw error;

      const result = data as unknown as ReceivePurchaseResult;
      if (!result || result.success !== true) {
        throw new Error('Falha ao registrar recebimento');
      }
      return result;
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['purchases-stats'] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['stock-summary'] });
      queryClient.invalidateQueries({ queryKey: ['product-warehouse-stock'] });
      // Virada para received gera payables CP-8 via trigger no motor
      queryClient.invalidateQueries({ queryKey: ['accounts-payable'] });
      queryClient.invalidateQueries({ queryKey: ['payables-stats'] });

      await auditService.logAction({
        action: 'ATUALIZAÇÃO',
        module: 'COMPRAS',
        entityType: 'compra',
        entityId: data.purchase_id,
        description: `Recebimento registrado - Novo status: ${data.new_status}`,
        details: {
          new_status: data.new_status,
          items_received: data.items_received,
          movements_created: data.movements_created,
          action_type: 'receipt'
        }
      });

      const statusLabel = data.new_status === 'received'
        ? 'totalmente recebida! Estoque lançado no depósito principal.'
        : 'parcialmente recebida!';
      toast({ title: `Compra ${statusLabel}` });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao registrar recebimento',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    purchases: query.data?.purchases || [],
    totalCount: query.data?.totalCount || 0,
    stats,
    isLoading: query.isLoading,
    pagination,
    setPagination,
    createPurchase: createMutation.mutate,
    createPurchaseAsync: createMutation.mutateAsync,
    updatePurchase: updateMutation.mutate,
    updatePurchaseAsync: updateMutation.mutateAsync,
    deletePurchase: deleteMutation.mutate,
    deletePurchaseAsync: deleteMutation.mutateAsync,
    receivePurchase: receiveItemsMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isReceiving: receiveItemsMutation.isPending,
  };
};
